import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    FormControlLabel,
    Switch,
    IconButton,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    Select,
    MenuItem,
    InputLabel,
    Checkbox
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api_service from "../../api/api_service";
import useAuth from '../../hooks/useAuth';
import dlpLayers from '../../data/dlpLayers.json';

const buildDefaultRanges = (thresholds = []) => {
    if (!thresholds.length) {
        return [{ min: '', max: '', score: 0 }];
    }

    const isAscending = thresholds[0] < thresholds[thresholds.length - 1];
    const ranges = [];

    if (isAscending) {
        ranges.push({ min: null, max: thresholds[0], score: 0 });
        for (let i = 0; i < thresholds.length - 1; i++) {
            ranges.push({
                min: thresholds[i],
                max: thresholds[i + 1],
                score: i + 1
            });
        }
        ranges.push({ min: thresholds[thresholds.length - 1], max: null, score: 0 });
    } else {
        ranges.push({ min: thresholds[0], max: null, score: 0 });
        for (let i = 0; i < thresholds.length - 1; i++) {
            const upper = thresholds[i];
            const lower = thresholds[i + 1];
            ranges.push({
                min: lower,
                max: upper,
                score: i + 1
            });
        }
        ranges.push({ min: null, max: thresholds[thresholds.length - 1], score: 0 });
    }

    return ranges.map(r => ({
        min: r.min === undefined || r.min === null ? '' : r.min,
        max: r.max === undefined || r.max === null ? '' : r.max,
        score: r.score
    }));
};

const DEFAULT_DYNAMIC_THRESHOLDS = [10000, 8000, 5000, 4000, 2500];

const shouldExcludeLayerFromCriteria = (datasetName = "") => {
    if (!datasetName) return true;
    const lower = datasetName.toLowerCase();
    if (lower.startsWith("dlp_substrate_") && lower !== "dlp_substrate_1m") {
        return true;
    }
    if (lower.startsWith("dlp_habitat_") && lower !== "dlp_habitat_mapping") {
        return true;
    }
    return false;
};

const DLP_LAYER_OPTIONS = dlpLayers
    .map(layer => {
        const filename = layer.filename || '';
        const datasetName = filename.replace(/\.geojson$/i, '');
        const styleCategories = layer.styleCategories || null;
        const categoryProperty = styleCategories?.property || null;
        const categoryOptions = categoryProperty && styleCategories?.categories
            ? Object.keys(styleCategories.categories).map((key) => ({
                label: key,
                value: key
            }))
            : null;
        return {
            id: layer.id,
            datasetName,
            label: layer.name || datasetName,
            categoryProperty,
            categoryOptions,
        };
    })
    .filter(option => option.datasetName && !shouldExcludeLayerFromCriteria(option.datasetName));

const createInitialConfig = (paramConfig) =>
    Object.entries(paramConfig).reduce((acc, [key, config]) => {
        acc[key] = {
            mode: 'legacy',
            thresholds: [...config.defaultThresholds],
            ranges: buildDefaultRanges(config.defaultThresholds),
            weight: config.defaultWeight,
            enabled: true,
            constraintType: 'distance',
            sourceDataset: key,
            label: config.label || key,
            filterValue: ''
        };
        return acc;
    }, {});

const slugifyValue = (value = '') => {
    return value
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '')
        || 'all';
};

const FormComponent = ({ selectedGeom, prefillRequest = null, onPrefillConsumed }) => {
    useAuth();

    // Define parameter configuration (can be moved outside if static)
    const PARAMETER_CONFIG = {
        Coastline: { defaultThresholds: [10000, 8000, 5000, 4000, 2500], defaultWeight: 0.1, label: "Coastline Distance (m)" },
        WindSpeed: { defaultThresholds: [5, 4.5, 4, 3, 2], defaultWeight: 0.1, label: "Wind Speed (m/s)" },
        Bathymetry: { defaultThresholds: [-30, -50, -60, -80, -100], defaultWeight: 0.1, label: "Bathymetry (m)" },
        Ports: { defaultThresholds: [10000, 8000, 5000, 4000, 2500], defaultWeight: 0.1, label: "Port Distance (m)" },
        ShippingRoutes: { defaultThresholds: [8000, 6000, 4000, 2000, 500], defaultWeight: 0.1, label: "Shipping Routes Distance (m)" },
        Posidonia: { defaultThresholds: [8000, 6000, 4000, 2000, 500], defaultWeight: 0.1, label: "Posidonia Distance (m)" }
    };

    const BASE_LAYER_OPTIONS = Object.entries(PARAMETER_CONFIG).map(([key, config]) => ({
        id: key,
        datasetName: key,
        label: config.label || key,
        defaultThresholds: config.defaultThresholds,
        categoryProperty: config.categoryProperty || null,
        categoryOptions: config.categoryOptions || null,
        source: 'base'
    }));
    const CHL_THRESHOLD_DEFAULTS = [10, 5, 2, 1, 0.5];
    const CHL_LAYER_OPTIONS = [
        { id: 'chl_djf', datasetName: 'Chl_DJF', label: 'Chlorophyll (DJF)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_mam', datasetName: 'Chl_MAM', label: 'Chlorophyll (MAM)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_jja', datasetName: 'Chl_JJA', label: 'Chlorophyll (JJA)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_son', datasetName: 'Chl_SON', label: 'Chlorophyll (SON)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_mean_djf', datasetName: 'Chl_Mean_DJF', label: 'Chlorophyll Seasonal Mean (DJF)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_mean_mam', datasetName: 'Chl_Mean_MAM', label: 'Chlorophyll Seasonal Mean (MAM)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_mean_jja', datasetName: 'Chl_Mean_JJA', label: 'Chlorophyll Seasonal Mean (JJA)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_mean_son', datasetName: 'Chl_Mean_SON', label: 'Chlorophyll Seasonal Mean (SON)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_max_djf', datasetName: 'Chl_Max_DJF', label: 'Chlorophyll Seasonal Max (DJF)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_max_mam', datasetName: 'Chl_Max_MAM', label: 'Chlorophyll Seasonal Max (MAM)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_max_jja', datasetName: 'Chl_Max_JJA', label: 'Chlorophyll Seasonal Max (JJA)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' },
        { id: 'chl_max_son', datasetName: 'Chl_Max_SON', label: 'Chlorophyll Seasonal Max (SON)', defaultThresholds: CHL_THRESHOLD_DEFAULTS, source: 'chl' }
    ];
    const ADDITIONAL_LAYER_OPTIONS = DLP_LAYER_OPTIONS.map((option) => ({
        ...option,
        defaultThresholds: DEFAULT_DYNAMIC_THRESHOLDS,
        source: 'dlp'
    }));
    const ALL_LAYER_OPTIONS = [...BASE_LAYER_OPTIONS, ...CHL_LAYER_OPTIONS, ...ADDITIONAL_LAYER_OPTIONS];
    const LAYER_OPTION_LOOKUP = ALL_LAYER_OPTIONS.reduce((acc, option) => {
        acc[option.datasetName] = option;
        return acc;
    }, {});

    const getInitialConfigurableParameters = () => createInitialConfig(PARAMETER_CONFIG);
    const baseParameterKeys = Object.keys(PARAMETER_CONFIG);

    const [formData, setFormData] = useState({
        message: '',
        description: '',
        areaOfInterest: '',
        areaOfInterest_index: '',
        input_polygon: '',
        use_case: 'aquaculture',
        model: { model_AUG: null, model_WS: null, model_PF: null }, // For main model selection
        configurableParameters: getInitialConfigurableParameters(), // For detailed parameter thresholds and weights
    });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedLayerId, setSelectedLayerId] = useState('');
    const [selectedLayerSubclass, setSelectedLayerSubclass] = useState('');
    const [customWeightsEnabled, setCustomWeightsEnabled] = useState(true);
    const replicationGuardRef = useRef(false);

    useEffect(() => {
        if (!prefillRequest || !prefillRequest.args) {
            return;
        }

        const payload = prefillRequest.args;
        const geometryDetails = prefillRequest.geometry;
        const targetUseCase = payload.use_case || 'aquaculture';
        const baseConfig = getInitialConfigurableParameters();
        Object.keys(baseConfig).forEach((key) => {
            baseConfig[key] = {
                ...baseConfig[key],
                enabled: false,
                weight: 0
            };
        });
        const mergedParams = targetUseCase === 'aquaculture' ? { ...baseConfig } : {};

        const normalizeRanges = (ranges = [], fallbackThresholds = []) => {
            if (!ranges.length) {
                return buildDefaultRanges(fallbackThresholds);
            }
            return ranges.map((range) => ({
                min: range.min === null || range.min === undefined ? '' : range.min,
                max: range.max === null || range.max === undefined ? '' : range.max,
                score: range.score ?? 0
            }));
        };

        Object.entries(payload.thresholds || {}).forEach(([paramKey, paramValue]) => {
            const hasBaseDefinition = Boolean(PARAMETER_CONFIG[paramKey]);
            const layerMeta = LAYER_OPTION_LOOKUP[paramKey];
            const providedThresholds = Array.isArray(paramValue.thresholds) && paramValue.thresholds.length
                ? [...paramValue.thresholds]
                : null;
            const fallbackThresholds = providedThresholds
                ? providedThresholds
                : (hasBaseDefinition ? [...PARAMETER_CONFIG[paramKey].defaultThresholds] : [...DEFAULT_DYNAMIC_THRESHOLDS]);
            const constraintType = (paramValue.constraint_type || 'distance').toLowerCase() === 'include'
                ? 'include'
                : 'distance';
            const existingEntry = mergedParams[paramKey] || {
                mode: 'legacy',
                thresholds: [...fallbackThresholds],
                ranges: buildDefaultRanges(fallbackThresholds),
                weight: 0,
                enabled: targetUseCase === 'aquaculture' ? false : true,
                label: layerMeta?.label || PARAMETER_CONFIG[paramKey]?.label || paramKey,
                isDynamic: !hasBaseDefinition,
                filterProperty: layerMeta?.categoryProperty || null,
                filterOptions: layerMeta?.categoryOptions || null,
                filterValue: '',
                constraintType: constraintType,
                sourceDataset: paramValue.source_dataset || layerMeta?.datasetName || paramKey
            };

            const isRangeMode = paramValue.mode === 'range' || (paramValue.ranges && paramValue.ranges.length > 0);
            const updatedEntry = {
                ...existingEntry,
                mode: isRangeMode ? 'range' : 'legacy',
                thresholds: isRangeMode
                    ? existingEntry.thresholds
                    : (Array.isArray(paramValue.thresholds) && paramValue.thresholds.length
                        ? [...paramValue.thresholds]
                        : existingEntry.thresholds),
                ranges: isRangeMode
                    ? normalizeRanges(paramValue.ranges || [], existingEntry.thresholds)
                    : existingEntry.ranges,
                weight: paramValue.weights ?? existingEntry.weight,
                enabled: targetUseCase === 'aquaculture'
                    ? (paramValue.enabled === false ? false : true)
                    : true,
                label: existingEntry.label || layerMeta?.label || paramKey,
                isDynamic: existingEntry.isDynamic || !hasBaseDefinition,
                filterProperty: paramValue.filter?.property || existingEntry.filterProperty,
                filterOptions: existingEntry.filterOptions || layerMeta?.categoryOptions || null,
                filterValue: paramValue.filter?.values
                    ? (paramValue.filter.values[0] ?? '')
                    : (existingEntry.filterValue || ''),
                constraintType,
                sourceDataset: paramValue.source_dataset || existingEntry.sourceDataset || paramKey
            };

            if (!hasBaseDefinition && !layerMeta) {
                updatedEntry.isDynamic = true;
            }

            mergedParams[paramKey] = updatedEntry;
        });

        const restoredModel = { model_AUG: null, model_WS: null, model_PF: null };
        Object.keys(restoredModel).forEach((key) => {
            if (payload.model && payload.model[key] !== undefined && payload.model[key] !== null) {
                restoredModel[key] = payload.model[key];
            }
        });

        const polygonSource = geometryDetails?.geometry?.coordinates
            ? geometryDetails.geometry.coordinates[0] || []
            : (Array.isArray(payload.input_polygon) ? payload.input_polygon : []);
        const coordinatesString = JSON.stringify(polygonSource);
        const shouldUseCustomWeights = targetUseCase === 'open-ended'
            ? (() => {
                const thresholdEntries = Object.values(payload.thresholds || {});
                const distanceWeights = thresholdEntries
                    .filter(item => (item.constraint_type || 'distance').toLowerCase() !== 'include')
                    .map(item => typeof item.weights === 'number' ? item.weights : 0);
                if (distanceWeights.length <= 1) {
                    return false;
                }
                const rounded = distanceWeights.map(w => Number(w.toFixed(4)));
                return new Set(rounded).size > 1;
            })()
            : true;

        replicationGuardRef.current = true;
        setFormData({
            message: payload.message || '',
            description: payload.description || '',
            areaOfInterest: geometryDetails?.name || payload.areaOfInterest || '',
            areaOfInterest_index: geometryDetails?.geom_id ?? payload.areaOfInterest_index ?? '',
            input_polygon: coordinatesString,
            use_case: targetUseCase,
            model: restoredModel,
            configurableParameters: mergedParams,
        });
        setSelectedLayerId('');
        setSelectedLayerSubclass('');
        setCustomWeightsEnabled(shouldUseCustomWeights);
        setSuccessMessage('');
        setErrorMessage('');
        if (typeof onPrefillConsumed === 'function') {
            onPrefillConsumed();
        }
    }, [prefillRequest]);

    useEffect(() => {
        if (replicationGuardRef.current) {
            replicationGuardRef.current = false;
            return;
        }

        if (selectedGeom && selectedGeom.geometry) {
            const coordinatesString = selectedGeom.geometry.coordinates
                ? JSON.stringify(selectedGeom.geometry.coordinates[0])
                : '[]';

            setFormData(prev => {
                const nextUseCase = prev.use_case || 'aquaculture';
                return {
                    ...prev,
                    description: prev.description,
                    areaOfInterest: selectedGeom.name || `ID: ${selectedGeom.geom_id}`,
                    areaOfInterest_index: selectedGeom.geom_id,
                    input_polygon: coordinatesString,
                    use_case: nextUseCase
                };
            });
            setSelectedLayerId('');
            setSelectedLayerSubclass('');
        } else {
            setFormData(prev => ({
                ...prev,
                message: '',
                description: '',
                input_polygon: '',
                areaOfInterest: '',
                areaOfInterest_index: '',
                use_case: 'aquaculture',
                model: { model_AUG: null, model_WS: null, model_PF: null },
                configurableParameters: getInitialConfigurableParameters(), // Reset to defaults
            }));
            setCustomWeightsEnabled(true);
            setSelectedLayerId('');
            setSelectedLayerSubclass('');
        }
    }, [selectedGeom]);

    const handleModelChange = (modelName) => {
        // Handles selection of main models like AUG, WS, PF
        setFormData((prevFormData) => {
            const updatedModels = { ...prevFormData.model };
            updatedModels[modelName] = updatedModels[modelName] === null ? "" : null;
            return {
                ...prevFormData,
                model: updatedModels,
            };
        });
    };

    const handleUseCaseChange = (event) => {
        const value = event.target.value;
        setFormData((prevFormData) => ({
            ...prevFormData,
            use_case: value,
            configurableParameters: value === 'open-ended'
                ? {}
                : getInitialConfigurableParameters(),
        }));
        setSelectedLayerId('');
        setSelectedLayerSubclass('');
        setCustomWeightsEnabled(value === 'open-ended' ? false : true);
    };

    const handleAddCriterion = () => {
        const option = ALL_LAYER_OPTIONS.find(opt => opt.id === selectedLayerId);
        if (!option) return;
        const hasSubclassOptions = Boolean(option.categoryOptions && option.categoryOptions.length);
        if (hasSubclassOptions && !selectedLayerSubclass) {
            setErrorMessage('Please choose a class for this layer before adding it as a criterion.');
            return;
        }

        const baseDataset = option.datasetName;
        const subclassValue = hasSubclassOptions ? selectedLayerSubclass : null;
        const subclassSlug = hasSubclassOptions && subclassValue ? slugifyValue(subclassValue) : null;
        const criterionKey = hasSubclassOptions
            ? `${baseDataset}__${subclassSlug}`
            : baseDataset;

        if (formData.configurableParameters[criterionKey]) {
            setErrorMessage('This class has already been added. Remove it first if you need to reconfigure.');
            return;
        }

        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            if (newParams[criterionKey]) {
                return prev;
            }
            const defaultThresholds = option.defaultThresholds || DEFAULT_DYNAMIC_THRESHOLDS;
            const subclassLabel = subclassValue
                ? (option.categoryOptions?.find(opt => opt.value === subclassValue)?.label || subclassValue)
                : null;
            const displayLabel = subclassLabel
                ? `${option.label} – ${subclassLabel}`
                : option.label;
            newParams[criterionKey] = {
                mode: 'legacy',
                thresholds: [...defaultThresholds],
                ranges: buildDefaultRanges(defaultThresholds),
                weight: 0,
                enabled: true,
                label: displayLabel,
                isDynamic: true,
                filterProperty: option.categoryProperty || null,
                filterOptions: option.categoryOptions || null,
                filterValue: subclassValue || '',
                constraintType: 'distance',
                sourceDataset: baseDataset
            };
            return { ...prev, configurableParameters: newParams };
        });
        setSelectedLayerId('');
        setSelectedLayerSubclass('');
        setErrorMessage('');
    };

    const handleRemoveParameter = (paramKey) => {
        if (formData.use_case !== 'open-ended' && baseParameterKeys.includes(paramKey)) return;
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            delete newParams[paramKey];
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleFilterSelection = (paramKey, value) => {
        const normalized = value === '' || value == null ? '' : value;
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            if (!newParams[paramKey]) return prev;
            newParams[paramKey] = { ...newParams[paramKey], filterValue: normalized };
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleConstraintTypeChange = (paramKey, value) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            if (!newParams[paramKey]) return prev;
            const nextConstraint = value === 'include' ? 'include' : 'distance';
            newParams[paramKey] = {
                ...newParams[paramKey],
                constraintType: nextConstraint,
                weight: nextConstraint === 'include' ? 0 : newParams[paramKey].weight
            };
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleWeightChange = (modelName, value) => {
        // This was for the main models (AUG, WS, PF) if they had weight inputs
        setFormData((prevFormData) => {
            const updatedModels = { ...prevFormData.model, [modelName]: value };
            return {
                ...prevFormData,
                model: updatedModels,
            };
        });
    };

    useEffect(() => {
        if (formData.use_case !== 'open-ended' || customWeightsEnabled) {
            return;
        }
        const entries = Object.entries(formData.configurableParameters);
        if (!entries.length) {
            return;
        }
        const nextParams = { ...formData.configurableParameters };
        const distanceKeys = entries.filter(([_, cfg]) => (cfg.constraintType || 'distance') !== 'include');
        const includeKeys = entries.filter(([_, cfg]) => (cfg.constraintType || 'distance') === 'include');
        let changed = false;
        if (distanceKeys.length) {
            const uniformWeight = 1 / distanceKeys.length;
            distanceKeys.forEach(([key, cfg]) => {
                const currentWeight = Number(cfg.weight) || 0;
                if (Math.abs(currentWeight - uniformWeight) > 1e-6) {
                    changed = true;
                    nextParams[key] = { ...cfg, weight: uniformWeight };
                }
            });
        }
        includeKeys.forEach(([key, cfg]) => {
            const currentWeight = Number(cfg.weight) || 0;
            if (currentWeight !== 0) {
                changed = true;
                nextParams[key] = { ...cfg, weight: 0 };
            }
        });
        if (changed) {
            setFormData(prev => ({ ...prev, configurableParameters: nextParams }));
        }
    }, [formData.configurableParameters, formData.use_case, customWeightsEnabled]);

    const usedSingleUseDatasets = new Set();
    Object.entries(formData.configurableParameters).forEach(([key, cfg]) => {
        const datasetName = cfg.sourceDataset || key;
        const hasSubclassOptions = Boolean(cfg.filterOptions && cfg.filterOptions.length);
        if (!hasSubclassOptions) {
            usedSingleUseDatasets.add(datasetName);
        }
    });

    const availableLayerOptions = ALL_LAYER_OPTIONS
        .filter(option => {
            if (option.categoryOptions && option.categoryOptions.length > 0) {
                return true;
            }
            const datasetName = option.datasetName;
            return !usedSingleUseDatasets.has(datasetName);
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    const selectedLayerOption = ALL_LAYER_OPTIONS.find(opt => opt.id === selectedLayerId) || null;
    const requiresSubclassSelection = Boolean(selectedLayerOption?.categoryOptions?.length);
    const canAddSelectedLayer = Boolean(
        selectedLayerOption &&
        (!requiresSubclassSelection || Boolean(selectedLayerSubclass))
    );

    const handleDetailedThresholdChange = (paramKey, index, value) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            const newThresholds = [...newParams[paramKey].thresholds];
            newThresholds[index] = value;
            newParams[paramKey] = { ...newParams[paramKey], thresholds: newThresholds };
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleDetailedWeightChange = (paramKey, value) => {
        if (formData.use_case === 'open-ended' && !customWeightsEnabled) {
            return;
        }
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            if (value === "" || !isNaN(parseFloat(value))) {
                newParams[paramKey] = { ...newParams[paramKey], weight: value }; 
            }
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleParameterToggle = (paramKey, enabled) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            newParams[paramKey] = { ...newParams[paramKey], enabled };
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleThresholdModeToggle = (paramKey, useRangeMode) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            const target = { ...newParams[paramKey] };
            target.mode = useRangeMode ? 'range' : 'legacy';
            if (useRangeMode && (!target.ranges || !target.ranges.length)) {
                target.ranges = buildDefaultRanges(target.thresholds);
            }
            newParams[paramKey] = target;
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleRangeFieldChange = (paramKey, index, field, value) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            const ranges = newParams[paramKey].ranges ? [...newParams[paramKey].ranges] : [];
            ranges[index] = { ...ranges[index], [field]: value };
            newParams[paramKey] = { ...newParams[paramKey], ranges };
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleAddRangeRow = (paramKey) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            const ranges = newParams[paramKey].ranges ? [...newParams[paramKey].ranges] : [];
            ranges.push({ min: '', max: '', score: 0 });
            newParams[paramKey] = { ...newParams[paramKey], ranges };
            return { ...prev, configurableParameters: newParams };
        });
    };

    const handleRemoveRangeRow = (paramKey, index) => {
        setFormData(prev => {
            const newParams = { ...prev.configurableParameters };
            const ranges = newParams[paramKey].ranges ? [...newParams[paramKey].ranges] : [];
            if (ranges.length <= 1) {
                return prev;
            }
            ranges.splice(index, 1);
            newParams[paramKey] = { ...newParams[paramKey], ranges };
            return { ...prev, configurableParameters: newParams };
        });
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage('');
        setErrorMessage('');

        if (!formData.areaOfInterest_index) {
            setErrorMessage('Please select an area of interest.');
            return;
        }
        const isAnyModelSelected = Object.values(formData.model).some(weight => weight !== null);
        if (!isAnyModelSelected) {
            setErrorMessage('Please select at least one model.');
            return;
        }
        const selectedModels = Object.entries(formData.model).filter(([_, value]) => value !== null);

        setLoading(true);

        try {
            const applicableParams = Object.entries(formData.configurableParameters).filter(
                ([_, paramValue]) => formData.use_case === 'open-ended' ? true : paramValue.enabled !== false
            );

            if (applicableParams.length === 0) {
                setErrorMessage(formData.use_case === 'open-ended'
                    ? 'Please add at least one criterion.'
                    : 'Please enable at least one parameter.');
                setLoading(false);
                return;
            }

            const distanceParams = applicableParams.filter(([_, paramValue]) => (paramValue.constraintType || 'distance') !== 'include');
            if (distanceParams.length === 0) {
                setErrorMessage('Add at least one distance-based criterion to run the optimization.');
                setLoading(false);
                return;
            }

            const totalWeight = distanceParams.reduce((sum, [_, paramValue]) => {
                const parsed = parseFloat(String(paramValue.weight));
                return sum + (isNaN(parsed) ? 0 : parsed);
            }, 0);

            if (Math.abs(totalWeight - 1) > 0.0001) {
                setErrorMessage('Weights for the included parameters must sum to 1.');
                setLoading(false);
                return;
            }

            const payload = {
                ...formData,
                input_polygon: JSON.parse(formData.input_polygon || '[]'),
                model: Object.fromEntries(
                    selectedModels.map(([key, value]) => [key, value === "" ? 0 : parseFloat(value)])
                ),
                thresholds: applicableParams.reduce((acc, [paramKey, paramValue]) => {
                    const weightVal = parseFloat(String(paramValue.weight));
                    const safeWeight = isNaN(weightVal) ? 0 : weightVal;
                    const constraintType = (paramValue.constraintType || 'distance');
                    if (paramValue.mode === 'range') {
                        const ranges = (paramValue.ranges || []).map(range => ({
                            min: range.min === '' || range.min === null ? null : parseFloat(String(range.min)),
                            max: range.max === '' || range.max === null ? null : parseFloat(String(range.max)),
                            score: range.score === '' || range.score === null ? 0 : parseFloat(String(range.score))
                        }));
                        acc[paramKey] = {
                            mode: 'range',
                            ranges,
                            weights: constraintType === 'include' ? 0 : safeWeight
                        };
                    } else {
                        const thresholds = (paramValue.thresholds || []).map(t => {
                            const parsed = parseFloat(String(t));
                            return isNaN(parsed) ? 0 : parsed;
                        });
                        acc[paramKey] = {
                            mode: 'legacy',
                            thresholds,
                            weights: constraintType === 'include' ? 0 : safeWeight
                        };
                    }
                    const normalizedFilter = Array.isArray(paramValue.filterValue)
                        ? paramValue.filterValue.slice(0, 1)
                        : (paramValue.filterValue ? [paramValue.filterValue] : []);
                    const filterPayload = (paramValue.filterProperty && normalizedFilter.length)
                        ? { property: paramValue.filterProperty, values: normalizedFilter }
                        : (paramValue.filter || null);
                    if (filterPayload) {
                        acc[paramKey].filter = filterPayload;
                    }
                    acc[paramKey].constraint_type = constraintType;
                    if (paramValue.sourceDataset) {
                        acc[paramKey].source_dataset = paramValue.sourceDataset;
                    }
                    return acc;
                }, {})
            };

            const response = await api_service.sendRequest(payload);
            console.log('Form submitted successfully:', response);
            setSuccessMessage('Your request was successfully submitted');
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrorMessage(error.message || 'Error submitting form. Please try again.');
            setSuccessMessage('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: '20px auto', 
            padding: { xs: 2, sm: 3 }, 
            maxWidth: '600px', 
            width: '100%', 
            boxSizing: 'border-box',
        }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center', width: '100%', mb: 3 }}> 
                Submit area for analysis
            </Typography>

            {errorMessage && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}
            {successMessage && !errorMessage && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }} onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3 
                }}
            >
                <TextField
                    label="Area of Interest"
                    name="areaOfInterest"
                    value={formData.areaOfInterest || ''} 
                    fullWidth 
                    size="small" 
                    variant="outlined" 
                    InputProps={{ readOnly: true }}
                />

                <TextField
                    label="Description (optional)"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    variant="outlined"
                />

                <FormControl component="fieldset">
                    <FormLabel component="legend">Use case</FormLabel>
                    <RadioGroup
                        row
                        value={formData.use_case}
                        onChange={handleUseCaseChange}
                    >
                        <FormControlLabel value="aquaculture" control={<Radio />} label="Aquaculture" />
                        <FormControlLabel value="open-ended" control={<Radio />} label="Open-ended" />
                    </RadioGroup>
                </FormControl>

                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Select Models and Thresholds
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {["model_AUG", "model_WS", "model_PF"].map((modelName) => (
                            <Box key={modelName} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Button
                                    variant={formData.model[modelName] !== null ? "contained" : "outlined"}
                                    onClick={() => handleModelChange(modelName)}
                                    fullWidth
                                >
                                    {modelName.replace('model_', '')}
                                </Button>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, mt: 2 }}>
                        Threshold Configuration
                    </Typography>
                    {formData.use_case === 'open-ended' && (
                        <Box sx={{ mb: 2, p: 2, border: '1px dashed #cfd8dc', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Add additional criteria from any of the available map layers:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                                <Autocomplete
                                    options={availableLayerOptions}
                                    value={selectedLayerOption}
                                    getOptionLabel={(option) => option.label || ''}
                                    isOptionEqualToValue={(option, value) => option.id === (value ? value.id : value)}
                                    onChange={(_, newValue) => {
                                        setSelectedLayerId(newValue ? newValue.id : '');
                                        setSelectedLayerSubclass('');
                                        setErrorMessage('');
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Layer" size="small" placeholder="Search layer" />
                                    )}
                                    sx={{ flex: 1, minWidth: 220 }}
                                    disableClearable={false}
                                    disabled={availableLayerOptions.length === 0}
                                />
                                {requiresSubclassSelection && (
                                    <FormControl sx={{ minWidth: 200 }} size="small">
                                        <InputLabel id="layer-subclass-label">Classes</InputLabel>
                                        <Select
                                            labelId="layer-subclass-label"
                                            label="Classes"
                                            displayEmpty
                                            value={selectedLayerSubclass || ''}
                                            onChange={(e) => {
                                                setSelectedLayerSubclass(e.target.value || '');
                                            }}
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return <em>Select class</em>;
                                                }
                                                const opt = selectedLayerOption?.categoryOptions?.find(o => o.value === selected);
                                                return opt?.label || selected;
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Select class</em>
                                            </MenuItem>
                                            {selectedLayerOption?.categoryOptions?.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddCriterion}
                                    disabled={!canAddSelectedLayer || availableLayerOptions.length === 0}
                                    sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
                                >
                                    Add Criterion
                                </Button>
                            </Box>
                            {availableLayerOptions.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    All available layers have already been added for this request.
                                </Typography>
                            )}
                            <FormControlLabel
                                sx={{ mt: 2 }}
                                control={
                                    <Checkbox
                                        checked={customWeightsEnabled}
                                        onChange={(e) => setCustomWeightsEnabled(e.target.checked)}
                                    />
                                }
                                label="Use custom weights"
                            />
                            {!customWeightsEnabled && (
                                <Typography variant="caption" color="text.secondary">
                                    When disabled, weights are redistributed uniformly across all distance-based criteria.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {Object.entries(formData.configurableParameters).map(([paramKey, paramData]) => {
                        const isParamEnabled = paramData.enabled !== false;
                        const isDynamicParam = !PARAMETER_CONFIG[paramKey];
                        const displayLabel = paramData.label || PARAMETER_CONFIG[paramKey]?.label || paramKey;
                        const constraintType = (paramData.constraintType || 'distance');
                        const showThresholds = constraintType !== 'include';
                        const weightDisabled = !isParamEnabled || constraintType === 'include' || (formData.use_case === 'open-ended' && !customWeightsEnabled);
                        return (
                        <Box key={paramKey} sx={{ mb: 2.5, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography variant="body1" gutterBottom sx={{ fontWeight: 'medium' }}>
                                    {displayLabel}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {formData.use_case !== 'open-ended' && (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isParamEnabled}
                                                    onChange={(e) => handleParameterToggle(paramKey, e.target.checked)}
                                                    size="small"
                                                />
                                            }
                                            label="Include"
                                        />
                                    )}
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={paramData.mode === 'range'}
                                                onChange={(e) => handleThresholdModeToggle(paramKey, e.target.checked)}
                                                size="small"
                                                disabled={!isParamEnabled || !showThresholds}
                                            />
                                        }
                                        label="Range mode"
                                    />
                                    {(isDynamicParam || formData.use_case === 'open-ended') && (
                                        <IconButton
                                            aria-label="Remove criterion"
                                            onClick={() => handleRemoveParameter(paramKey)}
                                            size="small"
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                            {formData.use_case === 'open-ended' && (
                                <FormControl component="fieldset" sx={{ mt: 1 }}>
                                    <FormLabel component="legend">Criterion mode</FormLabel>
                                    <RadioGroup
                                        row
                                        value={constraintType}
                                        onChange={(e) => handleConstraintTypeChange(paramKey, e.target.value)}
                                    >
                                        <FormControlLabel value="distance" control={<Radio size="small" />} label="Distance" />
                                        <FormControlLabel value="include" control={<Radio size="small" />} label="Membership" />
                                    </RadioGroup>
                                </FormControl>
                            )}
                            {paramData.filterOptions && (
                                <FormControl sx={{ mt: 1.5, minWidth: 200 }} size="small">
                                    <InputLabel id={`filter-select-${paramKey}`}>Classes</InputLabel>
                                    <Select
                                        labelId={`filter-select-${paramKey}`}
                                        label="Classes"
                                        displayEmpty
                                        value={paramData.filterValue || ''}
                                        onChange={(e) => handleFilterSelection(paramKey, e.target.value)}
                                        disabled={!isParamEnabled}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <em>All classes</em>;
                                            }
                                            const opt = paramData.filterOptions?.find((o) => o.value === selected);
                                            return opt?.label || selected;
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>All classes</em>
                                        </MenuItem>
                                        {paramData.filterOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {showThresholds ? paramData.mode === 'range' ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
                                    {paramData.ranges?.map((rangeRow, index) => (
                                        <Box
                                            key={`${paramKey}-range-${index}`}
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                alignItems: 'center',
                                                border: '1px dashed #e0e0e0',
                                                borderRadius: 1,
                                                p: 1
                                            }}
                                        >
                                            <TextField
                                                label="Min"
                                                value={rangeRow?.min ?? ''}
                                                onChange={(e) => handleRangeFieldChange(paramKey, index, 'min', e.target.value)}
                                                size="small"
                                                sx={{ width: { xs: '100%', sm: '25%' } }}
                                                inputProps={{ step: "any" }}
                                                disabled={!isParamEnabled}
                                            />
                                            <TextField
                                                label="Max"
                                                value={rangeRow?.max ?? ''}
                                                onChange={(e) => handleRangeFieldChange(paramKey, index, 'max', e.target.value)}
                                                size="small"
                                                sx={{ width: { xs: '100%', sm: '25%' } }}
                                                inputProps={{ step: "any" }}
                                                disabled={!isParamEnabled}
                                            />
                                            <TextField
                                                label="Score"
                                                value={rangeRow?.score ?? 0}
                                                onChange={(e) => handleRangeFieldChange(paramKey, index, 'score', e.target.value)}
                                                size="small"
                                                sx={{ width: { xs: '100%', sm: '20%' } }}
                                                inputProps={{ step: "any" }}
                                                disabled={!isParamEnabled}
                                            />
                                            <IconButton
                                                aria-label="Remove range"
                                                onClick={() => handleRemoveRangeRow(paramKey, index)}
                                                disabled={(paramData.ranges || []).length <= 1}
                                                size="small"
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddRangeRow(paramKey)}
                                        sx={{ alignSelf: 'flex-start' }}
                                        disabled={!isParamEnabled}
                                    >
                                        Add Range
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="caption" display="block" gutterBottom>
                                        Thresholds:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                        {paramData.thresholds.map((threshold, index) => (
                                            <TextField
                                                key={index}
                                                label={`T${index + 1}`}
                                                value={threshold}
                                                onChange={(e) => handleDetailedThresholdChange(paramKey, index, e.target.value)}
                                                size="small"
                                                sx={{ width: 'calc(20% - 8px)' }}
                                                inputProps={{ step: "any" }}
                                                disabled={!isParamEnabled}
                                            />
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    Membership filters remove cells directly and do not use numeric thresholds.
                                </Typography>
                            )}
                            <TextField
                                label="Weight"
                                type="number"
                                value={paramData.weight}
                                onChange={(e) => handleDetailedWeightChange(paramKey, e.target.value)}
                                fullWidth
                                size="small"
                                variant="outlined"
                                inputProps={{ step: "0.01", min: "0", max: "1" }} 
                                sx={{maxWidth: '200px'}}
                                disabled={weightDisabled}
                                helperText={constraintType === 'include' ? 'Weights are ignored for membership filters.' : undefined}
                            />
                        </Box>
                        );
                    })}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}> 
                    <Box sx={{ position: 'relative', width: '100%' }}> 
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || !formData.areaOfInterest_index}
                            fullWidth 
                        >
                            Submit Request
                        </Button>
                        {loading && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: 'primary.main',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box> 
    );
};

export default FormComponent;
